/**
 * This file gathers some of the differences between using Vue 2.6, Vue 2.7 and
 * Vue 3 in a common compatibility layer. This way, we need to modify fewer
 * source code files when upgrading to Vue 2.7 or Vue 3.
 */

import { invariant } from '@mimir/common/util/invariant';
import type { PropType as VuePropType, VueConstructor as VueCompatConstructor } from 'vue';
import Vue from 'vue';
import { getCurrentInstance as vueGetCurrentInstance } from 'vue';
export {
  computed,
  defineComponent,
  nextTick,
  reactive,
  readonly,
  ref,
  watch,
  onMounted,
  onUnmounted,
  onBeforeUnmount,
  onUpdated,
  defineAsyncComponent,
  provide,
  inject,
} from 'vue';
export type { ComputedRef, Ref } from 'vue';
export type PropType<T> = VuePropType<T>;
export type VueConstructor<V extends Vue = Vue> = VueCompatConstructor<V>;
import type { Breakpoint } from 'vuetify/types/services/breakpoint';
import type { Theme } from 'vuetify/types/services/theme';
/*VUE3*/// import { useDisplay as useVuetifyDisplay } from 'vuetify-next';
/*VUE2*/ const useVuetifyDisplay = () => ({} as Breakpoint);
/*VUE3*/// import { useTheme as useVuetifyTheme, type ThemeInstance } from 'vuetify';
/*VUE2*/ const useVuetifyTheme = () => ({} as any);
/*VUE3*/// type VuetifyThemeReturn = ThemeInstance;
/*VUE2*/ type VuetifyThemeReturn = Theme;


export const getCurrentInstance = vueGetCurrentInstance;

export function castRef<T extends VueCompatConstructor>(ref: any): InstanceType<T> {
  return ref as unknown as InstanceType<T>;
}

export function vueSet(object: any, propertyName: string | number, propertyValue: any) {
  Vue.set(object, propertyName, propertyValue);
}

export function vueDelete(object: any, propertyName: string) {
  Vue.delete(object, propertyName);
}

export function useRouter() {
  const vm = vueGetCurrentInstance();
  invariant(vm, 'useRouter() may only be used inside setup().');
  return vm.proxy.$router;
}

export function useRoute() {
  const vm = vueGetCurrentInstance();
  invariant(vm, 'useRoute() may only be used inside setup().');
  return vm.proxy.$route;
}

/**
 * This function replaces the content of the given existing object with
 * the content of the updated object. It is meant to be used when
 * we want to update an object without creating a new object reference,
 * for instance for updating an object that exists as a value in a map.
 */
export function vueUpdateObjectContent(existingObject: any, updatedObject: any) {
  const existingKeys = Object.keys(existingObject);
  const updatedKeys = Object.keys(updatedObject);

  // Let's remove keys that are no longer there
  for (const key of existingKeys) {
    if (!updatedKeys.includes(key)) {
      vueDelete(existingObject, key);
    }
  }

  // Let's set updated properties
  for (const key of updatedKeys) {
    vueSet(existingObject, key, updatedObject[key]);
  }
}

export function mdi(iconName: string) {
  if (import.meta.env.VUE_APP_V2) return 'mdi-' + iconName;
  else return 'mdi:mdi-' + iconName;
}

export function useDisplay() {
  return import.meta.env.VUE_APP_V2 ? vueGetCurrentInstance()!.proxy.$vuetify.breakpoint : useVuetifyDisplay();
}


export function useTheme(): VuetifyThemeReturn {
  return import.meta.env.VUE_APP_V2
    ? vueGetCurrentInstance()!.proxy.$vuetify.theme
    : useVuetifyTheme();
}

